import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AdminTopBar,
  ViewScroll,
  Button,
  IconButton,
  Avatar,
  Sym,
  RolePill,
  StatusPill,
  SectionLabel,
  InfoRow,
  MEMBER_STATUS_META,
  type MemberStatus,
} from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import { fetchUsers, approveUser, updateUser } from '../../api/users';
import { fetchFarms } from '../../api/farms';
import { fetchDevices } from '../../api/devices';
import { UserRole, type UserInfo, type FarmInfo, type DeviceInfo } from '@grosync/common';

/* ============================================================================
 * 회원관리 — 실제 백엔드 데이터 (fetchUsers / fetchFarms / fetchDevices).
 *
 * 도메인 매핑:
 *  - role: OPERATOR → 'manager(담당자)', 그 외 → 'user(사용자)'
 *  - status: 백엔드 users.status('pending'|'active'|'dormant') 를 그대로 사용.
 *    회원가입(POST /api/auth/request-access)은 'pending' 으로 저장되고,
 *    관리자가 승인(approveUser → PATCH status:'active')하면 활성화된다.
 *  - 지역/농장/장비: user→farm 직접 연결이 없으므로 farm.ownerId===user.id 로 파생.
 * ========================================================================= */

type RoleKey = 'manager' | 'user';
type FilterKey = 'all' | 'active' | 'pending' | 'dormant' | 'manager';

interface MemberRow {
  id: string;
  name: string;
  phone: string;
  email: string;
  region: string;
  role: RoleKey;
  status: MemberStatus;
  joined: string;
  last: string;
  farms: MemberFarm[];
  deviceCount: number;
}

interface MemberFarm {
  id: string;
  name: string;
  region: string;
  crop: string;
  devices: number;
  status: 'ok' | 'warn' | 'off';
}

/* ---- helpers ----------------------------------------------------------- */

const toRole = (role: UserInfo['role']): RoleKey =>
  role === UserRole.OPERATOR ? 'manager' : 'user';

const toMemberStatus = (u: UserInfo): MemberStatus => {
  // 백엔드 status('pending'|'active'|'dormant')를 그대로 사용. 구버전 응답(미존재) 대비 isActive 폴백.
  if (u.status === 'pending' || u.status === 'dormant' || u.status === 'active') return u.status;
  return u.isActive === false ? 'dormant' : 'active';
};

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtRelative = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return '방금 전';
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}일 전`;
  return fmtDate(iso);
};

const toFarmUiStatus = (deviceCount: number): 'ok' | 'warn' | 'off' =>
  // MOCK: farm 자체에는 상태 컬럼이 없어 장비 보유 여부로 간단히 파생.
  deviceCount > 0 ? 'ok' : 'off';

/** users + farms + devices → 화면 모델로 합성 */
function buildRows(users: UserInfo[], farms: FarmInfo[], devices: DeviceInfo[]): MemberRow[] {
  const devCountByFarm = new Map<string, number>();
  for (const d of devices) {
    if (d.farmId) devCountByFarm.set(d.farmId, (devCountByFarm.get(d.farmId) ?? 0) + 1);
  }

  return users.map((u) => {
    const ownedFarms = farms.filter((f) => f.ownerId === u.id);
    const memberFarms: MemberFarm[] = ownedFarms.map((f) => {
      const dc = devCountByFarm.get(f.id) ?? f.deviceCount ?? 0;
      const region = f.location?.description ?? '-';
      return {
        id: f.id,
        name: f.name,
        region,
        crop: f.cropType ?? '-',
        devices: dc,
        status: toFarmUiStatus(dc),
      };
    });
    const deviceCount = memberFarms.reduce((n, f) => n + f.devices, 0);
    const region = memberFarms[0]?.region ?? '-';
    return {
      id: u.id,
      name: u.name,
      phone: u.phone ?? '-',
      email: u.email ?? '-',
      region,
      role: toRole(u.role),
      status: toMemberStatus(u),
      joined: fmtDate(u.createdAt),
      last: fmtRelative(u.lastLoginAt),
      farms: memberFarms,
      deviceCount,
    };
  });
}

const GRID = '2.2fr 1.6fr 1.2fr 0.9fr 1fr 0.9fr 40px';

/* ---- MemberDrawer ------------------------------------------------------ */

const SELECT_STYLE: React.CSSProperties = {
  height: 40,
  padding: '0 12px',
  borderRadius: 12,
  border: '1px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface-container-lowest)',
  color: 'var(--md-sys-color-on-surface)',
  font: '400 13px/1 var(--gs-font-kr)',
};

function EditField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}): React.ReactElement {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 40,
          padding: '0 12px',
          borderRadius: 12,
          border: '1px solid var(--md-sys-color-outline-variant)',
          background: 'var(--md-sys-color-surface-container-lowest)',
          color: 'var(--md-sys-color-on-surface)',
          font: '400 13px/1 var(--gs-font-kr)',
          boxSizing: 'border-box',
        }}
      />
    </label>
  );
}

function MemberDrawer({
  m,
  user,
  onClose,
  onApprove,
  approving,
  onSave,
  saving,
  saveError,
}: {
  m: MemberRow | null;
  user: UserInfo | null;
  onClose: () => void;
  onApprove: (id: string) => void;
  approving: boolean;
  onSave: (id: string, body: { name?: string; email?: string; phone?: string; notes?: string; password?: string; role?: string; status?: MemberStatus }) => void;
  saving: boolean;
  saveError: string | null;
}): React.ReactElement | null {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '', password: '', role: UserRole.VIEWER as string, status: 'active' as MemberStatus });

  const resetForm = React.useCallback(() => {
    if (user) {
      setForm({
        name: user.name ?? '',
        email: user.email ?? '',
        phone: user.phone ?? '',
        notes: user.notes ?? '',
        password: '',
        role: user.role,
        status: (user.status ?? 'active') as MemberStatus,
      });
    }
  }, [user]);

  React.useEffect(() => {
    resetForm();
    setEditing(false);
  }, [user?.id, resetForm]);

  if (!m) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 20 }}
      />
      <aside
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          zIndex: 21,
          background: 'var(--md-sys-color-surface)',
          borderLeft: '1px solid var(--md-sys-color-outline-variant)',
          boxShadow: '-12px 0 40px -12px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'drawerIn 240ms cubic-bezier(.2,0,0,1)',
        }}
      >
        {/* header */}
        <div
          style={{
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderBottom: '1px solid var(--md-sys-color-outline-variant)',
          }}
        >
          <span style={{ flex: 1, font: '700 16px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
            회원 상세
          </span>
          {!editing && (
            <IconButton icon="edit" size={20} onClick={() => setEditing(true)} />
          )}
          <IconButton icon="close" size={22} onClick={onClose} />
        </div>

        <div
          className="gs-scroll-hide"
          style={{ flex: 1, overflowY: 'auto', padding: '22px 20px', display: 'flex', flexDirection: 'column', gap: 22 }}
        >
          {/* profile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar
              name={m.name}
              size={64}
              color="var(--gs-secondary-90)"
              style={{ font: '600 24px/1 var(--gs-font-kr)' }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ font: '700 20px/26px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                  {m.name}
                </span>
                <RolePill role={m.role} />
              </div>
              <div
                className="gs-num"
                style={{ marginTop: 4, font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}
              >
                회원 ID · {m.id}
              </div>
              <div style={{ marginTop: 8 }}>
                <StatusPill s={m.status} meta={MEMBER_STATUS_META} />
              </div>
            </div>
          </div>

          {/* contact */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SectionLabel style={{ marginBottom: 8 }}>연락처 정보</SectionLabel>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <EditField label="이름" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
                <EditField label="이메일" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} type="email" />
                <EditField label="휴대폰" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
                <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  아이디 · {user?.username ?? '-'} (수정 불가)
                </div>
              </div>
            ) : (
              <>
                <InfoRow icon="call" label="휴대폰" value={m.phone} />
                <InfoRow icon="mail" label="이메일" value={m.email} />
                <InfoRow icon="location_on" label="지역" value={m.region} />
                <InfoRow icon="event" label="가입일" value={m.joined} />
                <InfoRow icon="schedule" label="최근 접속" value={m.last} />
              </>
            )}
          </div>

          {/* 비고 (독립 섹션) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <SectionLabel>비고</SectionLabel>
            {editing ? (
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
                placeholder="관리자 메모"
                style={{
                  width: '100%',
                  resize: 'vertical',
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '1px solid var(--md-sys-color-outline-variant)',
                  background: 'var(--md-sys-color-surface-container-lowest)',
                  color: 'var(--md-sys-color-on-surface)',
                  font: '400 13px/18px var(--gs-font-kr)',
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              <div style={{ font: '400 13px/18px var(--gs-font-kr)', color: user?.notes ? 'var(--md-sys-color-on-surface)' : 'var(--md-sys-color-on-surface-variant)' }}>
                {user?.notes || '등록된 비고가 없습니다'}
              </div>
            )}
          </div>

          {/* 권한·상태·비밀번호 (수정 모드 전용) */}
          {editing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SectionLabel>권한 · 상태</SectionLabel>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>권한</span>
                <select
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  style={SELECT_STYLE}
                >
                  <option value={UserRole.VIEWER}>일반 사용자</option>
                  <option value={UserRole.OPERATOR}>담당자</option>
                  <option value={UserRole.ADMIN}>관리자</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>상태</span>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as MemberStatus }))}
                  style={SELECT_STYLE}
                >
                  <option value="active">활성</option>
                  <option value="pending">승인대기</option>
                  <option value="dormant">휴면</option>
                </select>
              </label>
              <EditField
                label="새 비밀번호 (변경 시에만 입력)"
                value={form.password}
                onChange={(v) => setForm((f) => ({ ...f, password: v }))}
                type="password"
              />
            </div>
          )}

          {/* farms */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SectionLabel style={{ marginBottom: 8 }}>보유 농장 · {m.farms.length}곳</SectionLabel>
            {m.farms.length ? (
              m.farms.map((f) => (
                <div
                  key={f.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 14,
                    background: 'var(--md-sys-color-surface-container-low)',
                    border: '1px solid var(--md-sys-color-outline-variant)',
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 11,
                      flexShrink: 0,
                      background: 'var(--md-sys-color-primary-container)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Sym name="agriculture" size={20} fill={1} style={{ color: 'var(--md-sys-color-on-primary-container)' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: '600 14px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                      {f.name}
                    </div>
                    <div
                      style={{ marginTop: 2, font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}
                    >
                      {f.region} · 장비 {f.devices}대 · {f.crop}
                    </div>
                  </div>
                  <StatusPill s={f.status} />
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: 18,
                  borderRadius: 14,
                  textAlign: 'center',
                  background: 'var(--md-sys-color-surface-container-low)',
                  font: '400 13px/18px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                }}
              >
                등록된 농장이 없습니다
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--md-sys-color-outline-variant)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {saveError && (
            <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-error)' }}>
              {saveError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            {editing ? (
              <>
                <Button variant="text" onClick={() => { resetForm(); setEditing(false); }} disabled={saving} style={{ flex: 1, height: 42 }}>
                  취소
                </Button>
                <Button
                  variant="filled"
                  icon="save"
                  disabled={saving}
                  onClick={() => {
                    const body: { name?: string; email?: string; phone?: string; notes?: string; password?: string; role?: string; status?: MemberStatus } = {
                      name: form.name,
                      email: form.email,
                      phone: form.phone,
                      notes: form.notes,
                      role: form.role,
                      status: form.status,
                    };
                    if (form.password.trim()) body.password = form.password;
                    onSave(m.id, body);
                  }}
                  style={{ flex: 1, height: 42 }}
                >
                  {saving ? '저장 중...' : '저장'}
                </Button>
              </>
            ) : m.status === 'pending' ? (
              <Button
                variant="filled"
                icon="how_to_reg"
                disabled={approving}
                onClick={() => onApprove(m.id)}
                style={{ flex: 1, height: 42 }}
              >
                {approving ? '승인 중...' : '가입 승인'}
              </Button>
            ) : (
              <Button variant="tonal" icon="mail" style={{ flex: 1, height: 42 }}>
                메시지 보내기
              </Button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

/* ---- View -------------------------------------------------------------- */

export default function MembersView(): React.ReactElement {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sel, setSel] = useState<MemberRow | null>(null);

  const queryClient = useQueryClient();
  const usersQ = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const farmsQ = useQuery({ queryKey: ['farms'], queryFn: fetchFarms });
  const devicesQ = useQuery({ queryKey: ['devices'], queryFn: fetchDevices });

  const approveM = useMutation({
    mutationFn: approveUser,
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      // 드로어가 열려 있으면 갱신된 상태를 반영
      setSel((cur) => (cur && cur.id === updated.id ? { ...cur, status: 'active' } : cur));
    },
  });

  const [saveError, setSaveError] = useState<string | null>(null);
  const saveM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updateUser>[1] }) => updateUser(id, body),
    onMutate: () => setSaveError(null),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setSel((cur) =>
        cur && cur.id === updated.id
          ? {
              ...cur,
              name: updated.name,
              email: updated.email ?? '-',
              phone: updated.phone ?? '-',
              role: toRole(updated.role),
              status: toMemberStatus(updated),
            }
          : cur,
      );
    },
    onError: (err: unknown) => setSaveError(err instanceof Error ? err.message : '저장에 실패했습니다'),
  });

  const rows = useMemo<MemberRow[]>(() => {
    if (!usersQ.data) return [];
    return buildRows(usersQ.data, farmsQ.data ?? [], devicesQ.data ?? []);
  }, [usersQ.data, farmsQ.data, devicesQ.data]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      active: rows.filter((m) => m.status === 'active').length,
      pending: rows.filter((m) => m.status === 'pending').length,
      dormant: rows.filter((m) => m.status === 'dormant').length,
      manager: rows.filter((m) => m.role === 'manager').length,
    }),
    [rows],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((m) => {
      const okF =
        filter === 'all'
          ? true
          : filter === 'manager'
            ? m.role === 'manager'
            : m.status === filter;
      const okQ =
        !needle ||
        [m.name, m.phone, m.email, m.region].some((s) => s.toLowerCase().includes(needle));
      return okF && okQ;
    });
  }, [rows, filter, q]);

  if (usersQ.isLoading) return <LoadingSpinner />;
  if (usersQ.error) return <PageError message={(usersQ.error as Error).message} />;

  const filters: { key: FilterKey; label: string; n: number }[] = [
    { key: 'all', label: '전체', n: counts.all },
    { key: 'active', label: '활성', n: counts.active },
    { key: 'pending', label: '승인대기', n: counts.pending },
    { key: 'dormant', label: '휴면', n: counts.dormant },
    { key: 'manager', label: '담당자', n: counts.manager },
  ];

  return (
    <>
      <AdminTopBar
        title="회원관리"
        subtitle={`총 ${counts.all}명 · 활성 ${counts.active} · 승인대기 ${counts.pending}`}
      />

      <ViewScroll gap={18}>
        <ContentToolbar
          search={q}
          onSearch={setQ}
          searchPlaceholder="이름 · 연락처 · 지역 검색"
          actions={
            <Button variant="filled" icon="person_add" style={{ height: 42 }}>
              회원 추가
            </Button>
          }
          filters={filters.map((f) => {
            const a = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  border: a ? 'none' : '1px solid var(--md-sys-color-outline-variant)',
                  background: a ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                  color: a ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                  font: `${a ? 600 : 500} 13px/1 var(--gs-font-kr)`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {f.label}
                <span className="gs-num" style={{ font: '700 12px/1 var(--gs-font-kr)', opacity: a ? 1 : 0.7 }}>
                  {f.n}
                </span>
              </button>
            );
          })}
        />

        {/* table */}
        <div
          style={{
            background: 'var(--md-sys-color-surface-container-lowest)',
            border: '1px solid var(--md-sys-color-outline-variant)',
            borderRadius: 18,
            overflow: 'hidden',
          }}
        >
          {/* head */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 12,
              padding: '13px 22px',
              borderBottom: '1px solid var(--md-sys-color-outline-variant)',
              background: 'var(--md-sys-color-surface-container-low)',
            }}
          >
            {['회원', '연락처', '지역', '농장·장비', '권한', '상태', ''].map((h, i) => (
              <span
                key={i}
                style={{
                  font: '600 12px/16px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  textAlign: i >= 3 && i < 6 ? 'center' : 'left',
                }}
              >
                {h}
              </span>
            ))}
          </div>

          {/* rows */}
          {filtered.map((m, i) => (
            <div
              key={m.id}
              className="gs-row"
              onClick={() => setSel(m)}
              style={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 12,
                padding: '14px 22px',
                alignItems: 'center',
                cursor: 'pointer',
                borderBottom: i === filtered.length - 1 ? 'none' : '1px solid var(--md-sys-color-outline-variant)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <Avatar name={m.name} size={40} color="var(--gs-secondary-90)" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ font: '600 14px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                    {m.name}
                  </div>
                  <div
                    className="gs-num"
                    style={{ marginTop: 2, font: '400 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}
                  >
                    {m.id} · {m.joined}
                  </div>
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="gs-num" style={{ font: '500 13px/17px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                  {m.phone}
                </div>
                <div
                  style={{
                    marginTop: 2,
                    font: '400 11px/15px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {m.email}
                </div>
              </div>
              <span style={{ font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                {m.region}
              </span>
              <div style={{ textAlign: 'center' }}>
                <span className="gs-num" style={{ font: '700 15px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                  {m.farms.length}
                </span>
                <span style={{ font: '400 12px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {' '}
                  · {m.deviceCount}대
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <RolePill role={m.role} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <StatusPill s={m.status} meta={MEMBER_STATUS_META} />
              </div>
              <Sym
                name="chevron_right"
                size={20}
                style={{ color: 'var(--md-sys-color-on-surface-variant)', justifySelf: 'end' }}
              />
            </div>
          ))}

          {!filtered.length && (
            <div
              style={{
                padding: 48,
                textAlign: 'center',
                font: '400 14px/20px var(--gs-font-kr)',
                color: 'var(--md-sys-color-on-surface-variant)',
              }}
            >
              조건에 맞는 회원이 없습니다
            </div>
          )}
        </div>
      </ViewScroll>

      <MemberDrawer
        m={sel}
        user={sel ? (usersQ.data?.find((u) => u.id === sel.id) ?? null) : null}
        onClose={() => setSel(null)}
        onApprove={(id) => approveM.mutate(id)}
        approving={approveM.isPending}
        onSave={(id, body) => saveM.mutate({ id, body })}
        saving={saveM.isPending}
        saveError={saveError}
      />
    </>
  );
}
